// eslint-disable-next-line @tseslint/no-shadow
import { ChangeDetectionStrategy, Component, EventEmitter, inject, input, InputSignal, model, ModelSignal, Output } from '@angular/core';
import { FaIconLibrary } from '@fortawesome/angular-fontawesome';
import { IconModule } from "@ogs-gmbh/ngx-m3-components";
import { OVERVIEW_HEADER_OPERATOR_STRATEGY, OverviewHeaderOperatorStrategy } from '../../types/overview-header-operator-strategy.type';
import { faGear } from '@fortawesome/pro-light-svg-icons';
import { OverviewSettingsMenuService } from '@shared/components/overview-header/services/overview-settings-menu.service';
import { MatIconButton } from '@angular/material/button';
// eslint-disable-next-line @tseslint/no-unused-vars
import { OverviewHeaderComponent } from '@shared/components/overview-header/overview-header.component';
// eslint-disable-next-line @tseslint/no-unused-vars
import { OverviewHeaderOperatorsComponent } from '../../overview-header-operators.component';

/**
 * Add this Component to the {@link OverviewHeaderOperatorsComponent | app-overview-header-operators}
 * to provide a settings operator inside the {@link OverviewHeaderComponent | app-overview-header}.
 * This Component allows the user to open the overview settings menu, either by emitting an event
 * or by delegating to the {@link OverviewSettingsMenuService}.
 *
 * This operator supports the following behaviors based on the applied strategy:
 * - `emit`: Emits an `openSettings` event that can be handled by the parent component
 * - `handle`: Opens the settings menu via the {@link OverviewSettingsMenuService}
 *
 * @example
 *
 * ```html
 * <app-overview-header>
 *   <app-overview-header-operators>
 *     <app-overview-header-settings />
 *   </app-overview-header-operators>
 * </app-overview-header>
 * ```
 */
@Component({
  selector: 'app-overview-header-settings',
  standalone: true,
  imports: [ IconModule, MatIconButton ],
  templateUrl: './overview-header-settings.component.html',
  styleUrl: './overview-header-settings.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class OverviewHeaderSettingsComponent {
  private readonly _faIconLibrary: FaIconLibrary = inject(FaIconLibrary);

  private readonly _overviewSettingsMenuService: OverviewSettingsMenuService = inject(OverviewSettingsMenuService);

  @Output() public readonly openSettings: EventEmitter<MouseEvent> = new EventEmitter<MouseEvent>();

  public strategy: InputSignal<OverviewHeaderOperatorStrategy> = input<OverviewHeaderOperatorStrategy>(OVERVIEW_HEADER_OPERATOR_STRATEGY.handle);

  protected isMenuExpanded: ModelSignal<boolean> = model(false);

  constructor () {
    this._faIconLibrary.addIcons(faGear);
  }

  protected _openSettings (mouseEvent: MouseEvent): void {
    if (this.strategy() === OVERVIEW_HEADER_OPERATOR_STRATEGY.emit)
      this.openSettings.emit(mouseEvent);

    else
      this._overviewSettingsMenuService.openMenu();
  }
}
